import { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Upload, ArrowUp, ArrowDown } from "lucide-react";
import { Select } from "@/components/admin/ui";

export function UploadButton({ onUploaded, accept, kind = "training", label = "Upload", testId }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try { const r = await api.post(`/upload/file?kind=${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } }); onUploaded(r.data); toast.success("Uploaded"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <>
      <input ref={ref} type="file" accept={accept} className="hidden" onChange={pick} data-testid={testId ? `${testId}-input` : undefined}/>
      <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => ref.current?.click()} data-testid={testId}><Upload className="w-3 h-3 mr-1"/>{busy ? "Uploading…" : label}</Button>
    </>
  );
}

const blankStep = () => ({ title: "", text: "", media: [] });

export function LessonEditor({ lesson, categories, onClose, onSaved }) {
  const [f, setF] = useState(lesson ? { ...lesson, tips: lesson.tips || [], warnings: lesson.warnings || [] } : {
    category: categories[0] || "Sushi Training", title: "", description: "", steps: [blankStep()], tips: [], warnings: [], video_url: "", pdf_url: "", published: true,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const setStep = (i, patch) => set("steps", f.steps.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const moveStep = (i, d) => { const s = [...f.steps]; const j = i + d; if (j < 0 || j >= s.length) return; [s[i], s[j]] = [s[j], s[i]]; set("steps", s); };
  const addMedia = (i, m) => setStep(i, { media: [...(f.steps[i].media || []), m] });

  const save = async () => {
    if (!f.title.trim()) return toast.error("Title is required");
    setSaving(true);
    const body = { category: f.category, title: f.title, description: f.description, steps: f.steps.filter((s) => s.title.trim()),
      tips: f.tips.filter(Boolean), warnings: f.warnings.filter(Boolean), video_url: f.video_url || "", pdf_url: f.pdf_url || "", published: !!f.published, store_id: f.store_id || null, display_order: f.display_order || 0 };
    try {
      if (lesson) await api.put(`/training/lessons/${lesson.id}`, body); else await api.post("/training/lessons", body);
      toast.success("Lesson saved"); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const listEditor = (key, placeholder) => (
    <div className="space-y-1">
      {f[key].map((t, i) => (
        <div key={i} className="flex gap-1"><Input value={t} onChange={(e) => set(key, f[key].map((x, j) => (j === i ? e.target.value : x)))} placeholder={placeholder} className="h-9"/>
          <button onClick={() => set(key, f[key].filter((_, j) => j !== i))} className="text-neutral-400 hover:text-red-600"><Trash2 className="w-4 h-4"/></button></div>
      ))}
      <Button size="sm" variant="ghost" onClick={() => set(key, [...f[key], ""])}><Plus className="w-3 h-3 mr-1"/>Add</Button>
    </div>
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="lesson-editor">
        <DialogHeader><DialogTitle>{lesson ? "Edit Lesson" : "New Lesson"}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-[200px_1fr] gap-3">
          <div><Label>Category</Label><Select value={f.category} onChange={(e) => set("category", e.target.value)} className="w-full" data-testid="lesson-category">{categories.map((c) => <option key={c}>{c}</option>)}</Select></div>
          <div><Label>Title</Label><Input value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="How to Make a California Roll" data-testid="lesson-title-input"/></div>
        </div>
        <div><Label>Description</Label><Textarea rows={2} value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="lesson-desc-input"/></div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>Training video (YouTube / Vimeo / direct link)</Label><Input value={f.video_url} onChange={(e) => set("video_url", e.target.value)} placeholder="https://youtu.be/…" data-testid="lesson-video-input"/></div>
          <div><Label>Downloadable SOP (PDF)</Label>
            <div className="flex gap-1"><Input value={f.pdf_url} onChange={(e) => set("pdf_url", e.target.value)} placeholder="https://…pdf"/><UploadButton accept="application/pdf" onUploaded={(d) => set("pdf_url", d.url)} label="PDF" testId="lesson-pdf-upload"/></div></div>
        </div>

        <div>
          <Label className="mb-2 block">Steps</Label>
          <div className="space-y-3">
            {f.steps.map((s, i) => (
              <div key={i} className="rounded-xl border border-neutral-200 p-3 space-y-2" data-testid={`step-editor-${i}`}>
                <div className="flex gap-2 items-center">
                  <span className="w-7 h-7 rounded-full bg-neutral-900 text-white text-xs flex items-center justify-center shrink-0">{i + 1}</span>
                  <Input value={s.title} onChange={(e) => setStep(i, { title: e.target.value })} placeholder={`Step ${i + 1} — Prepare Rice`} data-testid={`step-title-${i}`}/>
                  <button onClick={() => moveStep(i, -1)} className="text-neutral-400 hover:text-neutral-900"><ArrowUp className="w-4 h-4"/></button>
                  <button onClick={() => moveStep(i, 1)} className="text-neutral-400 hover:text-neutral-900"><ArrowDown className="w-4 h-4"/></button>
                  <button onClick={() => set("steps", f.steps.filter((_, j) => j !== i))} className="text-neutral-400 hover:text-red-600"><Trash2 className="w-4 h-4"/></button>
                </div>
                <Textarea rows={2} value={s.text} onChange={(e) => setStep(i, { text: e.target.value })} placeholder="Instructions…" data-testid={`step-text-${i}`}/>
                <div className="flex flex-wrap gap-2 items-center">
                  {(s.media || []).map((m, j) => (
                    <span key={j} className="inline-flex items-center gap-1 text-xs bg-neutral-100 rounded-full px-2 py-1">
                      {m.type === "photo" ? <img src={m.url} alt="" className="w-6 h-6 rounded object-cover"/> : m.type}
                      <button onClick={() => setStep(i, { media: s.media.filter((_, k) => k !== j) })} className="text-neutral-400 hover:text-red-600"><Trash2 className="w-3 h-3"/></button>
                    </span>
                  ))}
                  <UploadButton accept="image/*" label="Photo" onUploaded={(d) => addMedia(i, { type: "photo", url: d.url, caption: "" })} testId={`step-photo-${i}`}/>
                  <UploadButton accept="video/*" label="Video" onUploaded={(d) => addMedia(i, { type: "video", url: d.url, caption: "" })} testId={`step-video-${i}`}/>
                  <Button size="sm" variant="outline" onClick={() => { const u = window.prompt("Video link (YouTube/Vimeo):"); if (u) addMedia(i, { type: "embed", url: u, caption: "" }); }}>Embed link</Button>
                </div>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => set("steps", [...f.steps, blankStep()])} data-testid="step-add"><Plus className="w-3 h-3 mr-1"/>Add step</Button>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Tips</Label>{listEditor("tips", "Keep hands wet to avoid sticking")}</div>
          <div><Label>Warnings</Label>{listEditor("warnings", "Never leave fish above 41°F")}</div>
        </div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.published} onChange={(e) => set("published", e.target.checked)} data-testid="lesson-published"/> Published (visible to employees)</label>
        <Button onClick={save} disabled={saving} className="w-full rounded-xl bg-[hsl(var(--primary))] h-11" data-testid="lesson-save">{saving ? "Saving…" : "Save Lesson"}</Button>
      </DialogContent>
    </Dialog>
  );
}
